import {
  CircleAlert,
  CircleCheck,
  CircleDashed,
  Mail,
  RefreshCw,
  Truck,
} from "lucide-react";
import { useNotify } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { deliveryView, type DeliveryTone } from "./deliveryStatus";
import type { GulaOrder } from "./gulaTypes";
import { sendInstallerNotice } from "./installerNotify";
import type { OrderAction } from "./OrderCard";
import { refreshPacklink } from "./packlink";

const TONE_CLASS: Record<DeliveryTone, string> = {
  ok: "border-green-300 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950/40 dark:text-green-100",
  warn: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100",
  bad: "border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100",
  muted: "border-border bg-muted/50 text-foreground",
};

const TONE_ICON: Record<DeliveryTone, typeof Truck> = {
  ok: CircleCheck,
  warn: Truck,
  bad: CircleAlert,
  muted: CircleDashed,
};

const CONFIRMED_BY = [
  { value: "whatsapp", label: "Por WhatsApp" },
  { value: "phone", label: "Por teléfono" },
];

/**
 * Does the client have the unit? Checked against Packlink and the carrier, so
 * nobody tells the installer it is there before it is.
 */
export const DeliveryStatus = ({
  order,
  onAction,
  onRefresh,
}: {
  order: GulaOrder;
  onAction: OrderAction;
  onRefresh: () => void;
}) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const view = deliveryView(order);
  const Icon = TONE_ICON[view.tone];
  const installer = order.installer_name ?? "el instalador";

  const run = async (action: () => Promise<string>) => {
    setBusy(true);
    try {
      notify(await action(), { type: "info" });
      onRefresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const check = () =>
    run(async () => {
      const result = await refreshPacklink(order.id);
      return result.status === "delivered"
        ? "Packlink confirma la entrega"
        : "Comprobado en Packlink: aún no está entregado";
    });

  const tellInstaller = () =>
    run(async () => {
      const result = await sendInstallerNotice(order.id, order.installer_id);
      return `Avisado ${result.installer}: ya tiene el equipo`;
    });

  const confirmByClient = async (source: string) => {
    setConfirming(false);
    await onAction(order, order.stage, {
      received_at: new Date().toISOString(),
      received_source: source,
    });
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-md border p-2 text-xs",
        TONE_CLASS[view.tone],
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-semibold leading-tight">
          <Icon className="h-4 w-4 shrink-0" />
          {view.title}
        </span>
        {view.tracked && !order.received_at ? (
          <button
            type="button"
            aria-label="Comprobar ahora en Packlink"
            title="Comprobar ahora en Packlink"
            disabled={busy}
            onClick={check}
            className="opacity-70 hover:opacity-100 disabled:opacity-40"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} />
          </button>
        ) : null}
      </div>
      {view.detail ? <span>{view.detail}</span> : null}
      {view.event ? (
        <span className="opacity-80">Último aviso: {view.event}</span>
      ) : null}
      {view.checked ? <span className="opacity-60">{view.checked}</span> : null}

      {view.installerNeedsUpdate ? (
        <div className="mt-1 flex flex-col gap-1.5 border-t border-black/10 pt-1.5 dark:border-white/10">
          <span>
            {installer} recibió el parte antes de la entrega: avísale de que ya
            lo tiene para que cite.
          </span>
          <Button
            size="sm"
            className="h-7"
            onClick={tellInstaller}
            disabled={busy || !order.installer_email}
          >
            <Mail className="h-3.5 w-3.5" />
            {busy ? "Enviando…" : `Avisar a ${installer}: ya lo tiene`}
          </Button>
        </div>
      ) : null}

      {!order.received_at && order.stage === "installer_notified" ? (
        confirming ? (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span>¿Cómo lo confirmó el cliente?</span>
            {CONFIRMED_BY.map((option) => (
              <Button
                key={option.value}
                size="sm"
                variant="outline"
                className="h-6 px-2 text-xs"
                onClick={() => confirmByClient(option.value)}
              >
                {option.label}
              </Button>
            ))}
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="underline opacity-70 hover:opacity-100"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="self-start underline opacity-70 hover:opacity-100"
          >
            El cliente me ha dicho que ya lo tiene
          </button>
        )
      ) : null}
    </div>
  );
};
